import React, { useEffect, useState } from 'react';
/* MUI */
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  Collapse,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
/* MUI Icons */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpCenterOutlinedIcon from '@mui/icons-material/HelpCenterOutlined';
import OpacityOutlinedIcon from '@mui/icons-material/OpacityOutlined';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
/* Gen Types */
import {
  LeakDetector,
  useLeakDetectorDetailQuery,
  useLeakDetectorDetailUpdateSubscription,
} from '../../../../types/generated-types';

import {
  Battery,
  getBatteryLevel,
  formatBatteryVoltage,
} from '../../../ui/shared/battery';

/* Shared */
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import {
  DeviceAlerts,
  ToggleAlertDetailsButton,
} from '../shared-ui/alerts-info';
import { DeviceInfo, DeviceInformation } from '../shared-ui/device-info';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import {
  useDeviceHasAlerts,
  useDeviceAndSourceAreOnline,
} from '../../../system/AlertsManager';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export function LeakDetectorSensorIcon(leakStatus: any, deviceType: any) {
  let color;
  if (leakStatus === 'Leak Detected') {
    color = '#36AFA5';
  } else {
    color = 'rgba(0, 0, 0, 0.63)';
  }
  switch (deviceType) {
    case 'LeakDetector':
      return <OpacityOutlinedIcon sx={{ color, fontSize: '1.5rem' }} />;
    default:
      return <HelpCenterOutlinedIcon sx={{ color, fontSize: '1.5rem' }} />;
  }
}

export function LeakDetectorSensorComponent({
  deviceId,
}: {
  deviceId: string;
}) {
  const {
    data,
    // TODO: Loading_error: handle loading/error for leak detector detail query.
    // loading,
    // error
  } = useLeakDetectorDetailQuery({
    variables: { id: deviceId },
  });
  const [device, setDevice] = useState<Partial<LeakDetector>>();
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  const hasAlerts = useDeviceHasAlerts(device);
  const deviceOnline = useDeviceAndSourceAreOnline(device);

  useLeakDetectorDetailUpdateSubscription({
    variables: { ids: [deviceId] },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });
  useEffect(() => {
    if (data?.leakDetectorById) {
      setDevice(data.leakDetectorById as any);
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const batteryVoltageMinThresholdReached =
        device?.batteryVoltageMinThresholdReached?.value === true ||
        device?.batteryVoltageMinThresholdReached?.value?.toString() === 'true';

      const panelInfo: DeviceInformation = {
        Zone: device.zone?.name ?? 'Unnamed',
        'Zone ID': device.zone?._id ?? 'Unidentified',
        Source: device.source?.name ?? 'Unnamed source',
      };

      const devicesInfo: DeviceInformation[] = [
        {
          'Device ID': device.deviceId ?? device._id ?? 'Unidentified',
          Name: device.name ?? 'Unnamed',
          Type: device.typeDisplayName ?? 'Unknown',
          firmwareInfo: {
            Model:
              device.meta?.model || device.meta?.modelNumber || 'Unknown model',
            Version: device.meta?.version || 'Unknown version',
          },
          'Last Updated': moment(device.timestamp).fromNow(),
        },
      ];
      const voltageInfo: DeviceInformation = {
        'Battery Voltage': formatBatteryVoltage(device.voltage?.value),
        ...(!deviceOnline && device.voltage?.value != null
          ? {
              'Last known Battery Voltage': formatBatteryVoltage(
                device.voltage?.value,
              ),
            }
          : {}),
        'Battery Voltage Min Threshold': formatBatteryVoltage(
          device.batteryVoltageMinThreshold?.value,
        ),
        'Battery Voltage Min Threshold Reached':
          batteryVoltageMinThresholdReached ? 'Yes' : 'No',
      };

      setDeviceInfo([panelInfo, ...devicesInfo, voltageInfo]);
    }
  }, [device, deviceOnline]);

  const sensor1Triggered =
    device?.leakStatus1?.value === true ||
    device?.leakStatus1?.value?.toString() === 'true';
  const sensor2Triggered =
    device?.leakStatus2?.value === true ||
    device?.leakStatus2?.value?.toString() === 'true';

  const leakDetected = sensor1Triggered || sensor2Triggered;
  const deviceType = device?.type || 'unknown';

  const statusText = deviceOnline
    ? leakDetected
      ? `${device?.name || 'Unnamed sensor'} - Leak${
          sensor1Triggered && sensor2Triggered ? 's' : ''
        } detected on ${
          sensor1Triggered && sensor2Triggered
            ? 'both sensors'
            : 'sensor ' + (sensor1Triggered ? '1' : '2')
        }`
      : `${device?.name || 'Unnamed sensor'} is Dry`
    : `${device?.name || 'Unnamed sensor'} is Offline.`;

  const icon = LeakDetectorSensorIcon(leakDetected, deviceType);
  const [expanded, setExpanded] = React.useState(false);
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return device ? (
    <div
      style={{
        width: '100%',
      }}
    >
      <Card
        sx={{
          backgroundColor: '#ebf7f6',
          boxShadow: 1,
          p: 0,
          width: '100%',
          border: hasAlerts ? '2px solid red' : ' ',
          borderRadius: '4px',
          marginTop: '4px',
        }}
      >
        <CardHeader
          sx={{
            paddingTop: '0px',
            paddingLeft: '10px',
            paddingRight: '10ox',
            paddingBottom: '0px',
            border: '1px solid rgba(0,0,0, 0.1)',
          }}
          title={
            <Typography
              variant="h5"
              style={{ color: deviceOnline ? 'black' : 'red' }}
            >
              {statusText}
            </Typography>
          }
          avatar={
            <Avatar
              sx={{
                backgroundColor: 'transparent',
                padding: '0px',
                margin: '0px 2px 0px 2px',
              }}
            >
              {hasAlerts ? (
                <ToggleAlertDetailsButton
                  device={device}
                  expand={alertsExpanded}
                  onClick={(event) => {
                    event.preventDefault();
                    setAlertsExpanded(!alertsExpanded);
                  }}
                  buttonLabel="Show alerts"
                />
              ) : (
                icon
              )}
            </Avatar>
          }
          action={
            <Box>
              <div
                style={{
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <IconButton
                    sx={{
                      padding: '0px 6px 0px px',
                      // marginLeft: '-10px',
                    }}
                    edge="end"
                    aria-label="battery level button"
                  >
                    <Battery charge={getBatteryLevel(device.voltage?.value)} />
                  </IconButton>
                  <div
                    style={{
                      borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRightWidth: 'thin',
                      margin: '2px 6px',
                    }}
                  >
                    &nbsp;
                  </div>
                </div>
                <ExpandMore
                  expand={expanded}
                  onClick={handleExpandClick}
                  aria-expanded={expanded}
                  aria-label="show more"
                  sx={{ padding: '0px', margin: '0px' }}
                >
                  <ExpandMoreIcon sx={{ padding: '0px', margin: '0px' }} />
                </ExpandMore>
              </div>
            </Box>
          }
        />
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <DeviceInfo infoEntries={deviceInfo} />
        </Collapse>
        <DeviceAlerts device={device} alertsExpanded={alertsExpanded} />
      </Card>
    </div>
  ) : (
    <DeviceLoadingSkeleton />
  );
}
